import { doc, setDoc, deleteDoc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { getPrimarySavedPoster } from './primaryCollage';
import { getPrimaryCollagePosterNickname } from './mainCollageNickname';
import { normalizeSavedPostersList } from './savedPosterNormalize';

function sanitizeForFirestore(val) {
  if (val === undefined) return null;
  if (val === null || typeof val !== 'object') return val;
  if (val instanceof Date || (typeof val.toDate === 'function')) return val;
  if (Array.isArray(val)) return val.map(sanitizeForFirestore).filter((v) => v !== undefined);
  const out = {};
  for (const [k, v] of Object.entries(val)) {
    if (v === undefined) continue;
    const s = sanitizeForFirestore(v);
    if (s !== undefined) out[k] = s;
  }
  return out;
}

function normalizeProfilePhotoUrl(v) {
  if (v == null || typeof v !== 'string') return null;
  const t = v.trim();
  return t || null;
}

/**
 * `sharedPosters/{posterId}` often has newer FIND demographics than `userPublicPrimary.ad`
 * (profile mirrors merge there; denormalized preview can lag or omit fields). Use when opening
 * a partner collage from chat so the modal matches FIVE PRIZES / Reach Out data.
 */
export function mergeFeedDetailAdWithSharedPosterDemographics(ad, sharedData) {
  if (!ad || typeof ad !== 'object' || !sharedData || typeof sharedData !== 'object') return ad;
  const out = { ...ad };
  const hasAge = typeof out.selfAge === 'number' && Number.isFinite(out.selfAge);
  if (!hasAge) {
    const sAge = sharedData.selfAge;
    const num = typeof sAge === 'number' ? sAge : (sAge != null ? Number(sAge) : NaN);
    if (typeof num === 'number' && Number.isFinite(num)) {
      out.selfAge = Math.round(num);
    }
  }
  if (!out.selfGender || !['male', 'female', 'nb'].includes(out.selfGender)) {
    const g = sharedData.selfGender;
    if (g && ['male', 'female', 'nb'].includes(g)) out.selfGender = g;
  }
  const outLf = out.lookingForGenders;
  const hasLf = Array.isArray(outLf) && outLf.length > 0;
  if (!hasLf) {
    const lf = sharedData.lookingForGenders;
    if (Array.isArray(lf) && lf.length > 0) {
      const allowed = lf
        .map((x) => (typeof x === 'string' ? x.trim().toLowerCase() : ''))
        .filter((x) => x === 'male' || x === 'female' || x === 'nb');
      if (allowed.length) out.lookingForGenders = [...new Set(allowed)];
    }
  }
  if (!(out.auraColor && typeof out.auraColor === 'object')
    && sharedData.auraColor
    && typeof sharedData.auraColor === 'object') {
    out.auraColor = sharedData.auraColor;
  }
  return out;
}

/** Same shape as feed tile detail / wantedFeed mapping for the popup UI. */
export function mapSavedPosterToFeedDetailAd(poster, userId, userNameForAd) {
  if (!poster) return null;
  const createdAt =
    poster.createdAt instanceof Date
      ? poster.createdAt
      : (typeof poster.createdAt?.toDate === 'function' ? poster.createdAt.toDate() : new Date(0));
  return {
    id: poster.id,
    partnerCutout: poster.partnerCutout ?? null,
    heartPhotoUrl: poster.heartPhotoUrl ?? null,
    userPhotoUrl: poster.userPhotoUrl ?? null,
    swags: Array.isArray(poster.swags) ? poster.swags : [],
    places: Array.isArray(poster.places) ? poster.places : [],
    activityX: poster.activityX ?? '',
    activityY: poster.activityY ?? '',
    activityZ: poster.activityZ ?? null,
    userId: userId ?? poster.userId ?? null,
    userName: (userNameForAd && String(userNameForAd).trim()) || 'Someone',
    posterNickname: poster.posterNickname ?? 'The One',
    dominantPersonality: poster.dominantPersonality ?? null,
    alsoPrivateMatch:
      typeof poster.alsoPrivateMatch === 'boolean'
        ? poster.alsoPrivateMatch
        : (typeof poster.isPrivateCollage === 'boolean' ? poster.isPrivateCollage : undefined),
    friendLuv: typeof poster.friendLuv === 'boolean' ? poster.friendLuv : undefined,
    romanticLuv: typeof poster.romanticLuv === 'boolean' ? poster.romanticLuv : undefined,
    selfGender:
      poster.selfGender && ['male', 'female', 'nb'].includes(poster.selfGender) ? poster.selfGender : undefined,
    selfAge:
      typeof poster.selfAge === 'number' && Number.isFinite(poster.selfAge) ? poster.selfAge : undefined,
    lookingForGenders: (() => {
      const lf = poster.lookingForGenders;
      if (!Array.isArray(lf) || lf.length === 0) return undefined;
      const allowed = lf
        .map((g) => (typeof g === 'string' ? g.trim().toLowerCase() : ''))
        .filter((g) => g === 'male' || g === 'female' || g === 'nb');
      return allowed.length ? [...new Set(allowed)] : undefined;
    })(),
    createdAt,
    friendCollageShare:
      typeof poster.friendCollageShare === 'boolean' ? poster.friendCollageShare : undefined,
    friendFoxMaskPlaceholder:
      typeof poster.friendFoxMaskPlaceholder === 'boolean' ? poster.friendFoxMaskPlaceholder : undefined,
  };
}

/**
 * Re-reads users/{uid}: writes userPublicPrimary/{uid} for chat preview, and mirrors
 * primary collage → users.userPhotoUrl (navbar) and users.userPhotoOriginalUrl (unmask).
 * Call after any write that changes savedWantedPosters or primaryCollageId.
 */
export async function syncUserPublicPrimaryFromUserDocument(db, uid, displayName) {
  if (!uid) return;
  const userRef = doc(db, 'users', uid);
  const snap = await getDoc(userRef);
  if (!snap.exists()) return;
  const data = snap.data();
  const posters = normalizeSavedPostersList(Array.isArray(data?.savedWantedPosters) ? data.savedWantedPosters : []);
  const primaryCollageId = data?.primaryCollageId ?? null;
  const primary = getPrimarySavedPoster(posters, primaryCollageId);

  const profilePatch = {};
  const nextNavbarPhoto = primary ? normalizeProfilePhotoUrl(primary.userPhotoUrl) : null;
  const curNavbarPhoto = normalizeProfilePhotoUrl(data?.userPhotoUrl);
  if (nextNavbarPhoto !== curNavbarPhoto) {
    profilePatch.userPhotoUrl = nextNavbarPhoto;
  }
  const nextOriginal = primary ? normalizeProfilePhotoUrl(primary.userPhotoOriginalUrl) : null;
  const curOriginal = normalizeProfilePhotoUrl(data?.userPhotoOriginalUrl);
  if (nextOriginal !== curOriginal) {
    profilePatch.userPhotoOriginalUrl = nextOriginal;
  }
  if (Object.keys(profilePatch).length > 0) {
    await updateDoc(userRef, { ...profilePatch, updatedAt: new Date() });
  }

  const publicRef = doc(db, 'userPublicPrimary', uid);
  if (!primary) {
    await deleteDoc(publicRef).catch(() => {});
    return;
  }
  const nick = getPrimaryCollagePosterNickname(posters, primaryCollageId);
  const userNameForAd =
    (nick && String(nick).trim())
    || (displayName && String(displayName).trim())
    || 'Someone';
  const ad = mapSavedPosterToFeedDetailAd(primary, uid, userNameForAd);
  if (!ad) return;
  // Mirror the user's permanent vibe aura so matched friends can render it (e.g. the
  // mini aura behind their nickname on the daily-vibe-share grid).
  if (data?.auraColor && typeof data.auraColor === 'object') {
    ad.auraColor = data.auraColor;
  }
  await setDoc(publicRef, sanitizeForFirestore({ ad, updatedAt: serverTimestamp() }), { merge: true });
}
